import type { Profile } from '@liff/get-profile'
import liff from '@line/liff'
import * as Sentry from '@sentry/react'
import { postApiV1AuthLineToken } from '@wedding-tech/api-client'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { LINE_ENDPOINTS, lineClient } from './http'

interface JWTPayload {
  iss?: string
  sub?: string
  aud?: string
  exp?: number
  iat?: number
  auth_time?: number
  nonce?: string
  amr?: string[]
  name?: string
  picture?: string
  email?: string
}

export type LIFFContextType = {
  isReady: boolean
  isLoggedIn: boolean
  isInClient: boolean
  profile: Profile | null
  isFriend: boolean
  requestFriendship: () => Promise<void>
  decodedIdToken: JWTPayload | null
  initLiff: (liffId: string) => Promise<void>
}

export function extractEventId(): string {
  if (typeof window === 'undefined') return ''

  // 1. Pathname match: /e/:eventId
  const pathMatch = window.location.pathname.match(/\/e\/([^/]+)/)
  if (pathMatch?.[1]) return pathMatch[1]

  // 2. Query params match: ?eId=... or ?eventId=... or ?weddingId=...
  const urlParams = new URLSearchParams(window.location.search)
  const queryEventId =
    urlParams.get('eId') ||
    urlParams.get('eventId') ||
    urlParams.get('weddingId')
  if (queryEventId) return queryEventId

  // 3. LINE liff.state param match: ?liff.state=%2Fe%2F6a607ba0147ceb6a7ff0e205
  const liffState = urlParams.get('liff.state')
  if (liffState) {
    const decodedState = decodeURIComponent(liffState)
    const stateMatch = decodedState.match(/\/e\/([^/?#]+)/)
    if (stateMatch?.[1]) return stateMatch[1]
  }

  return ''
}

const DefaultLIFFContext: LIFFContextType = {
  isReady: false,
  isLoggedIn: false,
  isInClient: false,
  profile: null,
  isFriend: false,
  requestFriendship: () => Promise.resolve(),
  decodedIdToken: null,
  initLiff: async () => Promise.resolve(),
}

export const LIFFContext = createContext(DefaultLIFFContext)

export type UseLIFFContextOptions = {
  liffId?: string
  withLogin?: boolean
}

export const useLIFFContext = ({
  liffId = '',
  withLogin = true,
}: UseLIFFContextOptions = {}): LIFFContextType => {
  const initPromiseRef = useRef<Promise<void> | null>(null)

  const [isReady, setIsReady] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [isInClient, setIsInClient] = useState(false)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [decodedIdToken, setDecodedIdToken] = useState<JWTPayload | null>(null)

  const [isFriend, setIsFriend] = useState(false)

  const initializeLIFF = useCallback(
    (targetLiffId: string) => {
      if (!targetLiffId) {
        return Promise.resolve()
      }

      if (initPromiseRef.current) {
        return initPromiseRef.current
      }

      const promise = (async () => {
        // Safety Timeout: Force isReady to true after 10 seconds to avoid permanent loading
        const safetyTimeout = setTimeout(() => {
          if (!isReady) {
            console.warn('LIFF Initialization timed out. Forcing ready state.')
            setIsReady(true)
          }
        }, 10000)

        try {
          await liff.init({ liffId: targetLiffId })

          const isLoggedInQuery = liff.isLoggedIn()
          setIsLoggedIn(isLoggedInQuery)

          const isInClientQuery = liff.isInClient()
          setIsInClient(isInClientQuery)

          const os = liff.getOS()
          if (os) {
            document.documentElement.setAttribute('data-os', os)
          }

          // Handle external mobile browser redirect
          if (!isInClientQuery && os !== 'web') {
            const path = window.location.href.replace(
              window.location.origin,
              '',
            )
            const lineScheme = `line://app/${targetLiffId}?liff.state=${encodeURIComponent(path)}`
            window.location.replace(lineScheme)
            setTimeout(() => {
              window.close()
            }, 5000)
            return
          }

          // Automatic login if required
          if (withLogin && !isLoggedInQuery) {
            return liff.login({
              redirectUri: window.location.href,
            })
          }

          if (isLoggedInQuery) {
            try {
              const eventId = extractEventId()

              const [profileRes, friendshipRes, tokenRes] =
                await Promise.allSettled([
                  lineClient.get(LINE_ENDPOINTS.profile).json<Profile>(),
                  lineClient
                    .get(LINE_ENDPOINTS.friendship)
                    .json<{ friendFlag: boolean }>(),
                  eventId
                    ? postApiV1AuthLineToken({
                        body: {
                          accessToken: liff.getAccessToken() ?? '',
                          eventId,
                        },
                      })
                    : Promise.resolve(null),
                ])

              // Handle Profile
              if (profileRes.status === 'fulfilled') {
                const profileQuery = profileRes.value
                setProfile(profileQuery)

                // Set Sentry user
                Sentry.setUser({
                  id: profileQuery.userId,
                  username: profileQuery.displayName,
                })
              } else {
                console.error('Failed to fetch profile:', profileRes.reason)
              }

              // Handle Friendship
              if (friendshipRes.status === 'fulfilled') {
                setIsFriend(friendshipRes.value.friendFlag)
              }

              // Handle API Auth Token
              if (
                tokenRes.status === 'fulfilled' &&
                tokenRes.value &&
                'data' in tokenRes.value &&
                tokenRes.value.data?.token
              ) {
                if (typeof window !== 'undefined' && eventId) {
                  sessionStorage.setItem(
                    `liff_api_jwt_${eventId}`,
                    tokenRes.value.data.token,
                  )
                }
              } else if (tokenRes.status === 'rejected') {
                console.error(
                  'Failed to fetch API Auth Token:',
                  tokenRes.reason,
                )
              }

              const decodedIdToken = liff.getDecodedIDToken()
              if (decodedIdToken) {
                setDecodedIdToken(decodedIdToken)
                // Update Sentry user with email if available
                Sentry.setUser({ email: decodedIdToken.email })
              }
            } catch (error) {
              console.error('Data Fetching or Token Exchange Failed:', error)
            }
          }

          console.log('LIFF Initialized Successfully!')
        } catch (error) {
          console.error('LIFF Init Error:', error)
        } finally {
          clearTimeout(safetyTimeout)
          setIsReady(true)
        }
      })()

      initPromiseRef.current = promise
      return promise
    },
    [withLogin, isReady],
  )

  const requestFriendship = useCallback(async () => {
    try {
      const friendship = await liff.getFriendship()
      setIsFriend(friendship.friendFlag)

      if (friendship.friendFlag) {
        return
      }

      await liff.requestFriendship()

      const newFriendship = await liff.getFriendship()
      setIsFriend(newFriendship.friendFlag)

      if (newFriendship.friendFlag) {
        return
      }
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    if (!liffId) {
      return
    }

    initializeLIFF(liffId)
  }, [liffId, initializeLIFF])

  return {
    isReady,
    isLoggedIn,
    isInClient,
    profile,
    isFriend,
    requestFriendship,
    decodedIdToken,
    initLiff: initializeLIFF,
  }
}

export const useLIFF = () => {
  const context = useContext(LIFFContext)
  if (!context) {
    throw new Error('useLIFF must be used within LIFFProvider')
  }
  return context
}
